/**
 * Turning one stored image URL into the set of copies the browser can choose from.
 *
 * Uploads are resized server-side into a ladder of WebP files - 200, 400, 800
 * and 1600 pixels wide - written as siblings under a folder named for the
 * upload. Only the widest rung's URL is stored, in `ProductImage.url`, a
 * category's `image_url` or a theme section, because those are single `String`
 * columns and a manifest would need a migration on each of them. The rest of
 * the ladder is addressed by rewriting that one URL, which is safe precisely
 * because the widths are fixed and the folder is shared.
 *
 * Nothing is assumed about a URL that does not match the layout the pipeline
 * writes. A merchant can paste a link to their own CDN, and images uploaded
 * before the ladder existed are still single flat files; both fall through to a
 * plain `src` and render exactly as they always did.
 */

/** The rungs the backend writes. Must match `RENDITION_WIDTHS` in renditions.py. */
export const RENDITION_WIDTHS = [200, 400, 800, 1600] as const;

/** The widest rung, under a 32-character upload id: what the pipeline stores. */
const LADDERED_URL = /\/[0-9a-f]{32}\/1600\.webp$/;

/** The one copy beside the ladder that is not WebP. Mirrors `renditions.py`. */
export const LINK_PREVIEW_NAME = 'preview.jpg';

/**
 * The JPEG copy of a laddered image, or null if this URL has no ladder.
 *
 * For readers that are not browsers. A link unfurler - WhatsApp, and every
 * other chat client - fetches the address and tries to draw a thumbnail from
 * it; handed WebP it renders the card with the domain in it and no picture.
 * Null means "nothing better than what you already hold": an upload from before
 * the ladder existed, or a link a merchant pasted to their own CDN, which is as
 * likely to be a JPEG already as not. Callers send the stored URL in that case
 * rather than an address that would 404.
 */
export function linkPreviewUrl(url: string | null | undefined): string | null {
  if (!url || !LADDERED_URL.test(url)) return null;
  return `${url.slice(0, url.lastIndexOf('/'))}/${LINK_PREVIEW_NAME}`;
}

/** The blurred 32px copy beside the ladder. Mirrors `PLACEHOLDER_NAME` in `renditions.py`. */
export const PLACEHOLDER_NAME = 'placeholder.webp';

/**
 * The tiny blurred copy of a laddered image, to show while it loads, or null.
 *
 * A few hundred bytes, so fetching it costs next to nothing, and named by its
 * folder like every other sibling so no column has to carry it. Null for any
 * URL with no ladder - the caller keeps its flat colour rather than asking for
 * a file that would 404.
 */
export function placeholderUrl(url: string | null | undefined): string | null {
  if (!url || !LADDERED_URL.test(url)) return null;
  return `${url.slice(0, url.lastIndexOf('/'))}/${PLACEHOLDER_NAME}`;
}

export interface ImageSourceProps {
  src: string | undefined;
  srcSet?: string;
  sizes?: string;
}

/**
 * `<img>` props for a stored image, spread at the call site.
 *
 * `sizes` describes how wide the slot is *before* the image loads, which is the
 * only way the browser can pick a rung - it chooses while the layout is still
 * being computed. Getting it roughly right is what matters: too small and a
 * card looks soft, too large and the saving is lost.
 */
export function imageProps(url: string | null | undefined, sizes?: string): ImageSourceProps {
  if (!url) return { src: undefined };
  if (!LADDERED_URL.test(url)) return { src: url };

  const folder = url.slice(0, url.lastIndexOf('/'));
  return {
    src: url,
    srcSet: RENDITION_WIDTHS.map((width) => `${folder}/${width}.webp ${width}w`).join(', '),
    sizes,
  };
}

/**
 * A `sizes` hint for one cell of a storefront grid.
 *
 * The grid is two columns until `lg` whatever the merchant chose, so the hint
 * has to say so: a card is roughly half the viewport on a phone and a fraction
 * of the page column on a desktop. `--storefront-container-max` lets the page
 * grow to most of a wide screen (see `fluidContainerMax`), so the desktop arm
 * is a laptop's column up to 1440px and a share of the viewport beyond it.
 */
export function gridImageSizes(columns: number): string {
  const count = Math.max(columns, 1);
  const desktop = Math.round(1344 / count);
  return `(min-width: 1440px) calc(92vw / ${count}), (min-width: 1024px) ${desktop}px, 50vw`;
}

/** A slot whose width is fixed in CSS pixels, like an admin table's thumbnail. */
export function fixedImageSizes(cssPixels: number): string {
  return `${cssPixels}px`;
}
